import { Star, History, CalendarDays, Sparkles } from "lucide-react";
import HoverCard from "./HoverCard";
import { formatDate } from "./crmUi";
import type { RfpPrimeContact } from "../api/CrmApi";

interface Props {
  contact: RfpPrimeContact;
  onFlag: () => void;
  onUnflag: () => void;
}

// A carried-forward flag ("this person engaged well with us on a DIFFERENT
// RFP") is a materially different claim from a flag earned on the RFP a
// human is currently looking at, and a native `title=` tooltip can't render
// that distinction (icon, accent color, separate copy) — it can only show
// one flat string. See RfpPrimeContact.high_value_source_opportunity_id's
// docstring for why this provenance exists at all.
export default function HighValueTooltip({ contact, onFlag, onUnflag }: Props) {
  const isCarriedForward =
    contact.is_high_value &&
    !!contact.high_value_source_opportunity_id &&
    contact.high_value_source_opportunity_id !== contact.opportunity_id;
  // high_value_flagged_at is a naive-UTC *_at timestamp (crm_db._now()) —
  // utc=true shifts it to the viewer's local time, same as every other
  // *_at field in this feature (see crmUi.formatDate's own docstring).
  const flaggedDate = contact.high_value_flagged_at ? formatDate(contact.high_value_flagged_at, true) : null;

  const accent = !contact.is_high_value
    ? "border-border"
    : isCarriedForward
    ? "border-info-border"
    : "border-warning-border";

  return (
    <HoverCard
      accent={accent}
      trigger={
        <button onClick={() => (contact.is_high_value ? onUnflag() : onFlag())} className="flex-shrink-0">
          <Star
            className={`w-3.5 h-3.5 transition-colors ${
              contact.is_high_value ? "text-warning fill-warning" : "text-muted-foreground/40 hover:text-warning"
            }`}
          />
        </button>
      }
    >
      {!contact.is_high_value ? (
        <span className="flex items-center gap-2 text-xs text-muted-foreground">
          <Star className="w-3.5 h-3.5 flex-shrink-0" />
          Flag as high value
        </span>
      ) : (
        <span className="flex flex-col gap-1.5">
          <span className="flex items-center gap-1.5">
            {isCarriedForward ? (
              <History className="w-3.5 h-3.5 text-info flex-shrink-0" />
            ) : (
              <Sparkles className="w-3.5 h-3.5 text-warning flex-shrink-0" />
            )}
            <span className={`text-[11px] font-semibold uppercase tracking-wide ${isCarriedForward ? "text-info" : "text-warning-soft-foreground"}`}>
              {isCarriedForward ? "Carried Forward" : "High Value"}
            </span>
          </span>
          <span className="text-xs text-foreground leading-snug">
            {contact.notes || "No reason given."}
          </span>
          {isCarriedForward ? (
            <span className="text-[11px] text-muted-foreground leading-snug border-t border-border pt-1.5 mt-0.5">
              Originally flagged on <span className="text-foreground">{contact.high_value_source_opportunity_title || "another RFP"}</span>
              {flaggedDate && <> — {flaggedDate}</>}
              <br />
              <span className="italic">Not evidence of fit for this RFP.</span>
            </span>
          ) : flaggedDate ? (
            <span className="flex items-center gap-1 text-[11px] text-muted-foreground border-t border-border pt-1.5 mt-0.5">
              <CalendarDays className="w-3 h-3" /> Flagged {flaggedDate}
            </span>
          ) : null}
        </span>
      )}
    </HoverCard>
  );
}
